import React, { useState, useEffect, useCallback } from 'react';
import { BellOutlined } from '@ant-design/icons';
import { Modal, Badge, Spin, Empty } from 'antd';
import '../../../Assets/styles/LandingStyles/Promotions.css'
import { useLanguage } from '../../../Core/Translations/LanguageContext';
import Config from '../../../Core/Config.json';

// Interface for the processed promotion data (localized, as returned by the API)
interface Promotion {
    id: string;
    title: string;
    description: string;
    requirements: string;
    prizeImg: string | null;
    active: boolean;
}

const PromotionsNotification: React.FC = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [loading, setLoading] = useState(false);
    const [unreadPromotions, setUnreadPromotions] = useState<string[]>([]);

    const { language } = useLanguage();

    // Admin-managed promotions feed (replaces the old Google Sheets CSV).
    const API_URL = `${Config.apiBaseUrl}/api/Promotion/GetActive`;

    const getViewedPromotionIds = useCallback((): string[] => {
        const viewedPromotions = localStorage.getItem('viewedPromotions');
        return viewedPromotions ? JSON.parse(viewedPromotions) : [];
    }, []);

    const markPromotionAsRead = useCallback((id: string) => {
        setUnreadPromotions(prevUnread => {
            if (!prevUnread.includes(id)) {
                return prevUnread;
            }

            const newUnread = prevUnread.filter(promoId => promoId !== id);

            const displayedPromotionIds = promotions.map(p => p.id);
            const isDisplayed = displayedPromotionIds.includes(id);

            if (isDisplayed) {
                const viewedIds = getViewedPromotionIds();
                if (!viewedIds.includes(id)) {
                    localStorage.setItem('viewedPromotions', JSON.stringify([...viewedIds, id]));
                }
            }

            return newUnread;
        });
    }, [getViewedPromotionIds, promotions]);

    const fetchPromotions = useCallback(async () => {
        setLoading(true);
        const maxRetries = 3;
        let attempt = 0;

        while (attempt < maxRetries) {
            try {
                const langCode = language || 'en';
                const response = await fetch(`${API_URL}?lang=${encodeURIComponent(langCode)}`);
                if (!response.ok) throw new Error('Network response was not ok');

                const data = await response.json();
                const viewedIds = getViewedPromotionIds();

                const activePromotions: Promotion[] = (data?.promotions || []).map((p: any) => ({
                    id: p.id,
                    title: p.title,
                    description: p.description,
                    requirements: p.requirements,
                    prizeImg: p.prizeImg ?? null,
                    active: true,
                }));

                setPromotions(activePromotions);

                const unreadIds = activePromotions
                    .map(promo => promo.id)
                    .filter(id => !viewedIds.includes(id));

                setUnreadPromotions(unreadIds);

                setLoading(false);
                return;

            } catch (error) {
                console.error(`Attempt ${attempt + 1}: Error fetching promotions:`, error);
                attempt++;
                if (attempt < maxRetries) {
                    const delay = Math.pow(2, attempt) * 1000;
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }
        setLoading(false);
    }, [language, getViewedPromotionIds, API_URL]);

    useEffect(() => {
        fetchPromotions();
    }, [fetchPromotions]);

    const handleBellClick = () => {
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
    };

    const handlePromotionHover = (id: string) => {
        if (isModalVisible) {
            markPromotionAsRead(id);
        }
    };

    const getModalTitle = (lang: string) => {
        switch (lang) {
            case 'es': return 'Promociones Activas';
            case 'fr': return 'Promotions Actives';
            case 'hi': return 'सक्रिय प्रचार';
            case 'bn': return 'সক্রিয় প্রচার';
            case 'pt': return 'Promoções Ativas';
            case 'it': return 'Promozioni Attive';
            case 'ru': return 'Активные Акции';
            case 'ja': return 'アクティブプロモーション';
            case 'ko': return '활성 프로모션';
            default: return 'Active Promotions';
        }
    };

    const getEmptyDescription = (lang: string) => {
        switch (lang) {
            case 'es': return 'No hay promociones activas';
            case 'fr': return 'Aucune promotion active';
            case 'hi': return 'कोई सक्रिय प्रचार नहीं';
            case 'bn': return 'কোন সক্রিয় প্রচার নেই';
            case 'pt': return 'Nenhuma promoção ativa';
            case 'it': return 'Nessuna promozione attiva';
            case 'ru': return 'Нет активных акций';
            case 'ja': return 'アクティブなプロモーションはありません';
            case 'ko': return '활성 프로모션 없음';
            default: return 'No active promotions';
        }
    };

    const getRequirementsLabel = (lang: string) => {
        switch (lang) {
            case 'es': return 'Requisitos: ';
            case 'fr': return 'Exigences: ';
            case 'hi': return 'आवश्यकताएँ: ';
            case 'bn': return 'আবশ্যকতা: ';
            case 'pt': return 'Requisitos: ';
            case 'it': return 'Requisiti: ';
            case 'ru': return 'Требования: ';
            case 'ja': return '要件: ';
            case 'ko': return '요구사항: ';
            default: return 'Requirements: ';
        }
    };

    const isMainBellUnread = unreadPromotions.length > 0;

    return (
        <>
            <div className="promotions-bell-component">
                <div className="flex justify-start p-4 bg-gray-50 border-b border-gray-200">
                    <div className="promotions-bell-container">
                        <Badge count={isMainBellUnread ? 1 : 0} dot offset={[-5, 5]}>
                            <BellOutlined
                                className="promotions-bell-icon"
                                onClick={handleBellClick}
                            />
                        </Badge>
                    </div>
                </div>

                <Modal
                    title={getModalTitle(language)}
                    open={isModalVisible}
                    onCancel={handleModalClose}
                    footer={null}
                    width={600}
                    className="promotions-modal"
                >
                    {loading ? (
                        <div className="promotions-loading">
                            <Spin size="large" />
                        </div>
                    ) : promotions.length === 0 ? (
                        <Empty
                            description={getEmptyDescription(language)}
                        />
                    ) : (
                        <div className="promotions-list">
                            {promotions.map((promotion) => (
                                <div
                                    key={promotion.id}
                                    className="promotion-card-wrapper"
                                    onMouseEnter={() => handlePromotionHover(promotion.id)}
                                >
                                    <Badge
                                        dot
                                        count={unreadPromotions.includes(promotion.id) ? 1 : 0}
                                        className="promotion-dot-badge"
                                        offset={[0, 0]}
                                    >
                                        <div className="promotion-card">
                                            {promotion.prizeImg && (
                                                <div className="promotion-prize-image-container">
                                                    <img
                                                        src={promotion.prizeImg}
                                                        alt={`${promotion.title} prize`}
                                                        className="promotion-prize-image"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            <h3 className="promotion-title">{promotion.title}</h3>
                                            <p className="promotion-description">{promotion.description}</p>
                                            {promotion.requirements && (
                                                <div className="promotion-requirements">
                                                    <strong>
                                                        {getRequirementsLabel(language)}
                                                    </strong>
                                                    <span style={{ whiteSpace: 'pre-wrap' }}>
                                                        {promotion.requirements.replace(/ \| /g, '\n')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal>
            </div>
        </>
    );
};

export default PromotionsNotification;
